import { type HTMLAttributes, type ReactNode, useId } from 'react'
import { cn } from '../cn'

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Section heading (rendered as h2 and used to label the region). */
  title?: ReactNode
  /** Small uppercase line above the title ("THE CHANGE WE SEEK"). */
  eyebrow?: ReactNode
  /** Right-aligned header content: links, buttons. */
  actions?: ReactNode
  /** `tint` uses the wash background, as in the prototype's side panels. */
  variant?: 'surface' | 'tint'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  as?: 'section' | 'div' | 'article' | 'aside'
}

const PADDING = {
  none: '',
  sm: 'p-4',
  md: 'p-5 sm:p-6',
  lg: 'p-6 sm:p-8',
} as const

export function Card({
  title,
  eyebrow,
  actions,
  variant = 'surface',
  padding = 'md',
  as: Element = 'section',
  className,
  children,
  ...rest
}: CardProps) {
  const headingId = useId()
  const hasHeader = title !== undefined || eyebrow !== undefined || actions !== undefined
  return (
    <Element
      aria-labelledby={title !== undefined ? headingId : undefined}
      className={cn(
        'min-w-0 rounded-lg border',
        variant === 'tint'
          ? 'border-line bg-wash'
          : 'border-line bg-surface shadow-[0_1px_2px_rgb(22_62_58/0.04)]',
        PADDING[padding],
        className,
      )}
      {...rest}
    >
      {hasHeader ? (
        <header className="mb-5 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            {eyebrow !== undefined ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            {title !== undefined ? (
              <h2
                id={headingId}
                className="mt-1 text-lg font-semibold leading-snug tracking-[-0.01em] text-ink"
              >
                {title}
              </h2>
            ) : null}
          </div>
          {actions !== undefined ? (
            <div className="flex shrink-0 items-center gap-3 text-sm">{actions}</div>
          ) : null}
        </header>
      ) : null}
      {children}
    </Element>
  )
}

/** Small uppercase kicker used above headings. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={cn(
        'text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500',
        className,
      )}
    >
      {children}
    </p>
  )
}
